import { useCallback, useEffect } from 'react';

import { Modal, ModalBody, ModalHeader, Text } from '@change/ds-core-ui';

import { I18nOverrides, Translate } from '@change-corgi/core/react/i18n';

import I18N_PLACEHOLDERS from 'config/i18n/placeholders/petitionGamma/petition-gamma.yml';

import { useMobileSignFormValues } from '../../hooks/mobileSignFormContext/useMobileSignFormValues';
import { SignFormContextProvider } from '../../hooks/signFormContext';
import { useSignatureTracking } from '../../hooks/tracking/signature';
import { preloadSignForm, SignFormContainer } from '../signFormSunrise';

import type { ActionPanelSunriseVariant } from './MobileActionButton';
import { MobileModalContextDecorator } from './shared/MobileModalContextDecorator';
import type { MobileModalContext } from './shared/types';

const PRELOAD_SIGN_FORM_DELAY = 5000; // 5s

const MobileSignModalInner = ({
	isOpen,
	setIsOpen,
	variant,
}: {
	isOpen: boolean;
	setIsOpen: (isOpen: boolean) => void;
	variant: ActionPanelSunriseVariant;
}) => {
	const { mobileSignFormValues, updateMobileSignFormValues } = useMobileSignFormValues();

	const { trackClosedModal } = useSignatureTracking();

	const onClose = useCallback(() => {
		trackClosedModal();
	}, [trackClosedModal]);

	const variantProps = {
		v1: {
			fullScreenOnMobile: true,
			enableTransitionAnimations: false,
		},
		v2: {
			fullScreenOnMobile: false,
			enableTransitionAnimations: true,
		},
	};

	return (
		<Modal
			size="large"
			isOpen={isOpen}
			onClose={onClose}
			onOpenChange={setIsOpen}
			data-qa="sign-form-modal"
			// This is to avoid the sticky action button to be on top of the modal
			className="z-[10000]"
			{...variantProps[variant]}
		>
			<ModalHeader
				title={
					<Text variant="heading" size="x-small" fontWeight="bold">
						<Translate value="fe.components.petition_sign.complete_signature" />
					</Text>
				}
			/>
			<ModalBody>
				<div className="w-full px-1 py-1" data-qa="sign-form-modal-container">
					<I18nOverrides translations={I18N_PLACEHOLDERS}>
						<SignFormContextProvider insideMobileModal>
							<SignFormContainer previousState={mobileSignFormValues} onChange={updateMobileSignFormValues} />
						</SignFormContextProvider>
					</I18nOverrides>
				</div>
			</ModalBody>
		</Modal>
	);
};

export const MobileSignModal = ({
	context,
	isOpen,
	setIsOpen,
	variant,
}: {
	context: MobileModalContext;
	isOpen: boolean;
	setIsOpen: (isOpen: boolean) => void;
	variant: ActionPanelSunriseVariant;
}) => {
	return (
		<MobileModalContextDecorator {...context}>
			<MobileSignModalInner isOpen={isOpen} setIsOpen={setIsOpen} variant={variant} />
		</MobileModalContextDecorator>
	);
};

export function usePreloadMobileSignModal(): void {
	// preload the sign form chunk after a few seconds
	useEffect(() => {
		const timeout = window.setTimeout(() => {
			void preloadSignForm();
		}, PRELOAD_SIGN_FORM_DELAY);
		return () => window.clearTimeout(timeout);
	}, []);
}
